import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  PORTFOLIO_RADAR_CATEGORY_LABELS,
  type PortfolioRadarCategory,
  type PortfolioRadarSignalsState,
} from '../hooks/usePortfolioRadarSignals';
import type { InvestmentCompany, PortfolioRadarSignal, PortfolioRadarSignalSeverity } from '../types';
import { Favicon } from '../utils/favicon';
import styles from './PortfolioRadarPanel.module.css';

const TOP_SIGNALS_PER_CATEGORY = 3;

const CATEGORY_ORDER: PortfolioRadarCategory[] = [
  'needs_attention',
  'follow_on_candidate',
  'missing_update',
];

type PortfolioRadarPanelProps = {
  companies: InvestmentCompany[];
  state: PortfolioRadarSignalsState;
};

function severityLabel(severity: PortfolioRadarSignalSeverity): string {
  if (severity === 'urgent') return 'Urgent';
  if (severity === 'watch') return 'Watch';
  return 'Info';
}

function severityClassName(severity: PortfolioRadarSignalSeverity): string {
  if (severity === 'urgent') return styles.severityUrgent;
  if (severity === 'watch') return styles.severityWatch;
  return styles.severityInfo;
}

type CompanySummary = {
  name: string;
  websiteUrl?: string;
};

function RadarSignalCard({
  signal,
  company,
}: {
  signal: PortfolioRadarSignal;
  company: CompanySummary | undefined;
}) {
  return (
    <li>
      <Link to={signal.route} className={styles.signalCard}>
        <span
          className={`${styles.severityBadge} ${severityClassName(signal.severity)}`}
          aria-label={`Severity: ${severityLabel(signal.severity)}`}
        >
          {severityLabel(signal.severity)}
        </span>
        <p className={styles.signalTitle}>{signal.title}</p>
        {company?.name ? (
          <div className={styles.signalCompanyRow}>
            {company.websiteUrl ? (
              <Favicon
                websiteUrl={company.websiteUrl}
                size={24}
                alt=""
                className={`companyFavicon ${styles.companyLogo}`}
              />
            ) : (
              <span className={`companyFavicon ${styles.companyLogoPlaceholder}`} aria-hidden />
            )}
            <p className={styles.signalCompany}>{company.name}</p>
          </div>
        ) : null}
        {signal.reason ? <p className={styles.signalReason}>{signal.reason}</p> : null}
      </Link>
    </li>
  );
}

function RadarCategoryBlock({
  category,
  signals,
  companyById,
}: {
  category: PortfolioRadarCategory;
  signals: PortfolioRadarSignal[];
  companyById: Map<string, CompanySummary>;
}) {
  const topSignals = signals.slice(0, TOP_SIGNALS_PER_CATEGORY);
  const count = signals.length;

  return (
    <section className={styles.categoryBlock} aria-label={PORTFOLIO_RADAR_CATEGORY_LABELS[category]}>
      <div className={styles.categoryHead}>
        <h3 className={styles.categoryTitle}>{PORTFOLIO_RADAR_CATEGORY_LABELS[category]}</h3>
        <span className="app-counter" aria-label={`${count} signal${count === 1 ? '' : 's'}`}>
          {count}
        </span>
      </div>

      {topSignals.length === 0 ? (
        <p className={styles.categoryEmpty}>No signals in this bucket yet.</p>
      ) : (
        <ul className={styles.signalList}>
          {topSignals.map((signal) => (
            <RadarSignalCard
              key={signal.id}
              signal={signal}
              company={companyById.get(signal.companyId)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export function PortfolioRadarPanel({ companies, state }: PortfolioRadarPanelProps) {
  const { grouped, loading, error, signals } = state;

  const companyById = useMemo(() => {
    const map = new Map<string, CompanySummary>();
    for (const company of companies) {
      const name = company.name.trim();
      if (!name) continue;
      map.set(company.id, {
        name,
        websiteUrl: company.websiteUrl,
      });
    }
    return map;
  }, [companies]);

  const hasSignals = signals.length > 0;

  return (
    <section className={styles.section} aria-labelledby="portfolio-radar-heading">
      <div className={styles.panel}>

        {loading ? <p className={styles.loading}>Loading portfolio radar…</p> : null}

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        {!loading && !error && !hasSignals ? (
          <p className={styles.emptyState}>
            Forward founder emails or add manual reports to see portfolio signals here.
          </p>
        ) : null}

        {!loading && !error && hasSignals ? (
          <div className={styles.categoryGrid}>
            {CATEGORY_ORDER.map((category) => (
              <RadarCategoryBlock
                key={category}
                category={category}
                signals={grouped[category]}
                companyById={companyById}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
